import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';


// Review Cloud API base URL
const REVIEW_CLOUD_API_BASE_URL = import.meta.env.VITE_REVIEW_CLOUD_API_BASE_URL || 'http://localhost:8001/api/reviews';

interface QueryFilter {
  key?: string;
  field?: string;
  value: string;
  operator?: string;
}

interface UseReviewCloudDataProps {
  templateKey?: string;
  metricKey?: string;
  queryConfig?: {
    data_source?: string;
    endpoints?: {
      pending?: string;
      in_progress?: string;
      completed?: string;
    };
  };
  chartType?: string;
  filters?: QueryFilter[];
}

interface PendingTasksResponse {
  status: string;
  data: {
    payload: any[];
    count: number;
  };
}

export const useReviewCloudData = ({
  templateKey,
  metricKey,
  queryConfig,
  chartType,
  filters = []
}: UseReviewCloudDataProps) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize the data source to avoid unnecessary re-fetches
  const dataSource = queryConfig?.data_source;

  // Determine if this is a review cloud widget
  const isReviewCloudWidget = useMemo(() => {
    return templateKey === 'mp_actions' ||
      chartType === 'pending_tasks_card' ||
      metricKey?.startsWith('review_api:') ||
      dataSource === 'review_cloud';
  }, [templateKey, chartType, metricKey, dataSource]);

  // Get endpoint based on filter or default to pending
  const endpoint = useMemo(() => {
    const endpoints = queryConfig?.endpoints || {
      pending: '/portal/pending-tasks',
      in_progress: '/portal/inprogress-tasks',
      completed: '/portal/completed-tasks'
    };

    // Check for task_status filter
    const statusFilter = filters?.find(f => f.key === 'task_status' || f.field === 'task_status');
    const statusValue = statusFilter?.value?.toLowerCase() || 'pending';

    if (statusValue === 'in_progress' || statusValue === 'in progress') {
      return endpoints.in_progress || '/portal/inprogress-tasks';
    }
    if (statusValue === 'completed') {
      return endpoints.completed || '/portal/completed-tasks';
    }
    return endpoints.pending || '/portal/pending-tasks';
  }, [queryConfig?.endpoints, filters]);

  useEffect(() => {
    if (!isReviewCloudWidget) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Get auth token from localStorage (synced by shell app)
        const token = localStorage.getItem('access_token');

        if (!token) {
          console.warn('[useReviewCloudData] No auth token available');
          setError('Authentication required');
          setLoading(false);
          return;
        }

        // Normalize endpoint - strip /api/reviews prefix if present since it's already in the base URL
        const normalizedEndpoint = endpoint.startsWith('/api/reviews')
          ? endpoint.replace('/api/reviews', '')
          : endpoint;
        // Strip /v1 from base URL — review cloud endpoints are not versioned
        const baseUrl = REVIEW_CLOUD_API_BASE_URL.replace(/\/v1\/?$/, '');
        const fullUrl = `${baseUrl}${normalizedEndpoint}`;

        console.log('[useReviewCloudData] Fetching from:', fullUrl);

        const response = await axios.get<PendingTasksResponse>(fullUrl, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          timeout: 10000
        });

        // Handle response.data structure
        // Expected format: { status: "success", data: { payload: [...], count: number } }
        const responseData = response.data;

        // Transform response to component format
        if (responseData?.data?.payload) {
          console.log('[useReviewCloudData] Received', responseData.data.count, 'tasks');
          setData(responseData.data.payload);
        } else if (Array.isArray(responseData?.data?.data)) {
          // Handle nested { data: { data: [...] } } structure (e.g., /learning-participants)
          setData(responseData.data.data);
        } else if (Array.isArray(responseData?.data)) {
          setData(responseData.data);
        } else if (Array.isArray(responseData)) {
          setData(responseData);
        } else {
          console.warn('[useReviewCloudData] Unexpected response format:', responseData);
          setData([]);
        }
      } catch (err: any) {
        console.error('[useReviewCloudData] Error fetching data:', err);

        if (err.response?.status === 401) {
          setError('Session expired. Please log in again.');
        } else if (err.response?.status === 403) {
          setError('You do not have permission to view this data.');
        } else if (err.code === 'ECONNABORTED') {
          setError('Request timed out. Please try again.');
        } else if (err.message?.includes('Network Error')) {
          setError('Unable to connect to the server.');
        } else {
          setError(err.response?.data?.message || 'Failed to load pending tasks');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isReviewCloudWidget, endpoint]);

  return { data, loading, error };
};
